import { zonedDate } from '../../../../utils/date-time';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Agendamento, StatusHorario } from '../../Enum';
import { formatBRL, getScheduleStatusLabel, getScheduleTone, getScheduleTotal } from '../../cliente/List/helpers';
import { UserService } from '../../../configuracoes/usuario/Service';
import { formatMes } from '../../../recorrencias/helpers';

const userService = new UserService();

const PAGE_SIZE = 10;
// Intervalo calculado sobre as visitas recentes: o habito antigo do cliente nao diz muito hoje.
const FREQUENCY_WINDOW = 7;

type ClientHistoryProps = {
    clientId: string;
    currentId: string;
    attendantId: string;
    attendantName?: string;
    referenceDate: string | Date;
    // A consulta traz todos os agendamentos do cliente; so roda ao abrir a aba.
    active: boolean;
};

// Mediana dos intervalos em dias, para uma falta longa nao distorcer o ritmo do cliente.
const getFrequencyDays = (visits: Agendamento[]) => {
    const days = Array.from(new Set(visits.slice(0, FREQUENCY_WINDOW).map((visit) => zonedDate(visit.startAt, visit.timezone).format('YYYY-MM-DD'))));
    if (days.length < 3) return null;
    const gaps = days.slice(1).map((day, index) => moment(days[index]).diff(moment(day), 'days')).sort((a, b) => a - b);
    const middle = Math.floor(gaps.length / 2);
    return gaps.length % 2 ? gaps[middle] : Math.round((gaps[middle - 1] + gaps[middle]) / 2);
};

const formatFrequency = (days: number) => {
    if (days < 60) return `A cada ${days} ${days === 1 ? 'dia' : 'dias'}`;
    return `A cada ${Math.round(days / 30)} meses`;
};

const getFavoriteService = (visits: Agendamento[]) => {
    const counts = new Map<string, number>();
    visits.forEach((visit) => {
        const name = visit.service?.name;
        if (name) counts.set(name, (counts.get(name) || 0) + 1);
    });
    const [favorite] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    return favorite && favorite[1] > 1 ? { name: favorite[0], count: favorite[1] } : null;
};

const getServiceNames = (agendamento: Agendamento) =>
    [agendamento.service?.name || 'Serviço não informado', ...(agendamento.servicosExtras || []).map((extra) => extra.name)]
        .filter(Boolean)
        .join(' + ');

export function ClientHistory({ clientId, currentId, attendantId, attendantName, referenceDate, active }: ClientHistoryProps) {
    const [items, setItems] = useState<Agendamento[]>([]);
    const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
    const [retry, setRetry] = useState(0);
    const [onlyAttendant, setOnlyAttendant] = useState(false);
    const [visible, setVisible] = useState(PAGE_SIZE);
    const loadedKey = useRef('');
    const reference = new Date(referenceDate).getTime();

    useEffect(() => {
        const key = `${clientId}:${retry}`;
        if (!active || loadedKey.current === key) return;
        loadedKey.current = key;
        let current = true;
        setStatus('loading');
        userService
            .getAgendamentoByClientId(clientId)
            .then(({ data }: { data: Agendamento[] }) => {
                if (!current) return;
                setItems(Array.isArray(data) ? data : []);
                setStatus('ready');
            })
            .catch((error: unknown) => {
                console.error(error);
                if (current) setStatus('error');
            });
        return () => {
            current = false;
        };
    }, [active, clientId, retry]);

    const { past, visits, cancelled, withAttendant, next, frequency, favorite } = useMemo(() => {
        const time = (item: Agendamento) => new Date(item.startAt).getTime();
        const isCancelled = (item: Agendamento) => item.status === StatusHorario.CANCELADO;
        const others = items.filter((item) => item._id !== currentId);
        const pastItems = others.filter((item) => time(item) < reference).sort((a, b) => time(b) - time(a));
        const pastVisits = pastItems.filter((item) => !isCancelled(item));
        return {
            past: pastItems,
            visits: pastVisits,
            cancelled: pastItems.length - pastVisits.length,
            withAttendant: pastVisits.filter((item) => item.attendant?.uid === attendantId).length,
            next: others.filter((item) => time(item) > reference && !isCancelled(item)).sort((a, b) => time(a) - time(b))[0],
            frequency: getFrequencyDays(pastVisits),
            favorite: getFavoriteService(pastVisits),
        };
    }, [items, currentId, reference, attendantId]);

    const hasOtherAttendants = past.some((item) => item.attendant?.uid !== attendantId);
    const filtered = onlyAttendant ? past.filter((item) => item.attendant?.uid === attendantId) : past;
    const shown = filtered.slice(0, visible);
    const months = useMemo(() => {
        const groups: { key: string; label: string; items: Agendamento[] }[] = [];
        shown.forEach((item) => {
            const key = zonedDate(item.startAt, item.timezone).format('YYYY-MM');
            const group = groups[groups.length - 1];
            if (group?.key === key) group.items.push(item);
            else groups.push({ key, label: formatMes(String(item.startAt), item.timezone), items: [item] });
        });
        return groups;
    }, [shown]);

    if (status === 'loading') {
        return (
            <div className="sf-detail-history-loading" role="status" aria-label="Carregando histórico do cliente">
                <div className="sf-skeleton h-20" />
                <div className="sf-skeleton h-16" />
                <div className="sf-skeleton h-16" />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="sf-detail-group-wrap">
                <div className="sf-detail-group sf-detail-empty" role="alert">
                    <p>Não foi possível carregar o histórico do cliente.</p>
                    <button type="button" className="sf-detail-link mt-1" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button>
                </div>
            </div>
        );
    }

    const firstName = attendantName?.trim().split(/\s+/)[0] || 'este profissional';
    const lastVisit = visits[0];
    const nextRow = next && (
        <div className="sf-detail-row">
            <div className="sf-detail-row-label">Próximo agendamento</div>
            <div className="sf-detail-row-value">{zonedDate(next.startAt, next.timezone).format('DD/MM/YYYY [às] HH:mm')}</div>
        </div>
    );

    if (!past.length) {
        return (
            <div className="sf-detail-group-wrap">
                <div className="sf-detail-group">
                    <p className="sf-detail-empty">Nenhum atendimento antes deste.</p>
                    {nextRow}
                </div>
            </div>
        );
    }

    return (
        <>
            {!!visits.length && (
                <dl className="sf-detail-stats">
                    <div>
                        <dt>Atendimentos</dt>
                        <dd>{visits.length}</dd>
                        <p>{withAttendant === visits.length ? `Todos com ${firstName}` : `${withAttendant} com ${firstName}`}</p>
                    </div>
                    <div>
                        <dt>Última visita</dt>
                        <dd>{zonedDate(lastVisit.startAt, lastVisit.timezone).from(moment(reference))}</dd>
                        <p>{zonedDate(lastVisit.startAt, lastVisit.timezone).format('DD/MM/YYYY')}</p>
                    </div>
                    {frequency !== null && (
                        <div>
                            <dt>Frequência</dt>
                            <dd>{formatFrequency(frequency)}</dd>
                            <p>Últimas visitas</p>
                        </div>
                    )}
                </dl>
            )}

            {(favorite || next || cancelled > 0) && (
                <div className="sf-detail-group-wrap">
                    <div className="sf-detail-group">
                        {favorite && (
                            <div className="sf-detail-row">
                                <div className="sf-detail-row-label">Mais pedido</div>
                                <div className="sf-detail-row-value">{favorite.name} · {favorite.count}×</div>
                            </div>
                        )}
                        {nextRow}
                        {cancelled > 0 && (
                            <div className="sf-detail-row">
                                <div className="sf-detail-row-label">Cancelamentos</div>
                                <div className="sf-detail-row-value">{cancelled}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {hasOtherAttendants && (
                <div className="sf-detail-history-filter">
                    <div className="sf-segmented" role="group" aria-label="Filtrar atendimentos por profissional">
                        {[false, true].map((value) => (
                            <button
                                key={String(value)}
                                type="button"
                                aria-pressed={onlyAttendant === value}
                                onClick={() => {
                                    setOnlyAttendant(value);
                                    setVisible(PAGE_SIZE);
                                }}
                            >
                                {value ? `Com ${firstName}` : 'Todos'}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {!filtered.length && (
                <div className="sf-detail-group-wrap">
                    <p className="sf-detail-group sf-detail-empty">Nenhum atendimento com {firstName} antes deste.</p>
                </div>
            )}

            {months.map((month) => (
                <section key={month.key} className="sf-detail-group-wrap">
                    <div className="sf-detail-group-title"><h3>{month.label}</h3></div>
                    <ul className="sf-detail-group sf-detail-past-list">
                        {month.items.map((item) => {
                            const start = zonedDate(item.startAt, item.timezone);
                            const total = getScheduleTotal(item);
                            const isCancelled = item.status === StatusHorario.CANCELADO;
                            return (
                                <li key={item._id} className={`sf-detail-past-item ${isCancelled ? 'is-cancelled' : ''}`}>
                                    <span className="sf-detail-past-date" aria-hidden="true">
                                        <span>{start.format('ddd').replace('.', '')}</span>
                                        <strong>{start.format('DD')}</strong>
                                    </span>
                                    <div className="min-w-0">
                                        <h4><span className="sr-only">{start.format('DD/MM/YYYY')}: </span>{getServiceNames(item)}</h4>
                                        <p className="sf-detail-past-meta">
                                            {start.format('HH:mm')}
                                            {!onlyAttendant && item.attendant?.name ? ` · ${item.attendant.name}` : ''}
                                        </p>
                                        {!!item.observation && <p className="sf-detail-past-note"><b>Atendimento</b>{item.observation}</p>}
                                        {!!item.observationClient && <p className="sf-detail-past-note"><b>Cliente</b>{item.observationClient}</p>}
                                        {!!item.cancellation?.observation && <p className="sf-detail-past-note"><b>Cancelamento</b>{item.cancellation.observation}</p>}
                                    </div>
                                    <div className="sf-detail-past-value">
                                        {total !== null && <p>{formatBRL(total)}</p>}
                                        {/* Concluido e o estado comum: so o que foge dele ganha etiqueta. */}
                                        {item.status === StatusHorario.CONCLUIDO
                                            ? <span className="sr-only">Concluído</span>
                                            : <span className="sf-tag" data-tone={getScheduleTone(item.status)}>{getScheduleStatusLabel(item)}</span>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}

            {filtered.length > visible && (
                <div className="mt-3 text-center">
                    <button type="button" className="sf-detail-link" onClick={() => setVisible((value) => value + PAGE_SIZE)}>
                        Ver mais ({filtered.length - visible})
                    </button>
                </div>
            )}
        </>
    );
}
